import { mapLabelValue } from '@@viewHelpers/string.js';
import { dateTsToFr } from '@@viewHelpers/date.js';
import PropTypes from 'prop-types';
import {
  __,
  always,
  append,
  compose,
  concat,
  cond,
  equals,
  filter,
  ifElse,
  map,
  pathOr,
  pipe,
  prop,
  propEq,
  propOr,
  T,
  tail,
  toString
} from 'ramda';
import formatCommissionNumber from '@@viewHelpers/formatCommissionPercent.mjs';
import { isNilOrEmpty, mapIndexed } from '@@serverHelpers/index.mjs';
import { offerCodeEnum } from '@@viewEnums';
import { Row } from '@fasstech/middle-design-system';

const SummaryIndividualHealthCard = ({ effectDate, pack, getMiddleText, offerCode }) => {
  const pricings = compose(filter(propEq('isSelected', true)), propOr([], 'pricings'))(pack);
  const supplementaryCoverPricings = tail(pricings);
  const basePricing = prop(0, pricings);

  const supplementaryCoverPricingsOptions = isNilOrEmpty(supplementaryCoverPricings)
    ? [[getMiddleText('CONTRACT_RESPONSIBLE_COMPLEMENTARY_LABEL'), '-']]
    : mapIndexed(
      (supplementaryCoverPricing, index) =>
        pipe(prop('moduleLabel'), (label) => [
          index ? '' : getMiddleText('CONTRACT_RESPONSIBLE_COMPLEMENTARY_LABEL'),
          label
        ])(supplementaryCoverPricing),
      supplementaryCoverPricings
    );

  const groupedSupplementaryCover = isNilOrEmpty(supplementaryCoverPricings)
    ? [[getMiddleText('CONTRACT_RESPONSIBLE_COMPLEMENTARY_LABEL'), '-']]
    : [
      [
        getMiddleText('CONTRACT_RESPONSIBLE_COMPLEMENTARY_LABEL'),
        map(prop('moduleLabel'), supplementaryCoverPricings)
      ]
    ];

  const addGroupedSupplementaryCoverIfNotEmpty = (arr) => {
    const hasValidSupplementaryCover = !isNilOrEmpty(supplementaryCoverPricings);

    if (hasValidSupplementaryCover) {
      return concat(arr, groupedSupplementaryCover);
    }
    return arr;
  };

  const options = cond([
    [
      equals(offerCodeEnum.INDIVIDUAL_PROTECTION_DAILY_BENEFIT),
      always(
        append([getMiddleText('PROCESSDEVENTE_EFFECT_DATE_PLACEHOLDER'), dateTsToFr(effectDate)])(
          []
        )
      )
    ],
    [
      equals(offerCodeEnum.SELF_EMPLOYED_WORKER_PROTECTION),
      always(
        compose(
          append([
            getMiddleText('CONTRACT_REF_LABEL'),
            pipe(
              pathOr(0, ['commissions', 0, 'rate']),
              toString,
              ifElse(equals('0'), always('-'), formatCommissionNumber)
            )(pack)
          ]),
          concat(__, supplementaryCoverPricingsOptions),
          append([getMiddleText('PROCESSDEVENTE_EFFECT_DATE_PLACEHOLDER'), dateTsToFr(effectDate)]),
          append([getMiddleText('CONTRACT_PRODUCT_LABEL'), propOr('-', 'moduleLabel', basePricing)])
        )([])
      )
    ],
    [
      equals(offerCodeEnum.INDIVIDUAL_HEALTH),
      always(
        compose(
          addGroupedSupplementaryCoverIfNotEmpty,
          append([getMiddleText('PROCESSDEVENTE_EFFECT_DATE_PLACEHOLDER'), dateTsToFr(effectDate)]),
          append([getMiddleText('CONTRACT_PRODUCT_LABEL'), propOr('-', 'moduleLabel', basePricing)])
        )([])
      )
    ],
    [
      T,
      always(
        compose(
          append([
            getMiddleText('CONTRACT_REF_LABEL'),
            pipe(
              pathOr(0, ['commissions', 0, 'rate']),
              toString,
              ifElse(equals('0'), always('-'), formatCommissionNumber)
            )(pack)
          ]),
          addGroupedSupplementaryCoverIfNotEmpty,
          append([getMiddleText('PROCESSDEVENTE_EFFECT_DATE_PLACEHOLDER'), dateTsToFr(effectDate)]),
          append([getMiddleText('CONTRACT_PRODUCT_LABEL'), propOr('-', 'moduleLabel', basePricing)])
        )([])
      )
    ]
  ])(offerCode);

  const leftColumn = mapLabelValue(options).slice(0, Math.ceil(mapLabelValue(options).length / 2));
  const rightColumn = mapLabelValue(options).slice(Math.ceil(mapLabelValue(options).length / 2));

  return (
    <>
      {/* desktop */}
      <div className={'hidden md:flex flex-col gap-4 p-4'}>
        <div className="grid grid-cols-2 gap-4 w-full p-4">
          <div className="flex flex-col gap-4 w-full">
            {leftColumn.map((item, index) => (
              <Row key={index} item={item} index={index} />
            ))}
          </div>

          <div className="flex flex-col gap-4 w-full">
            {rightColumn.map((item, index) => (
              <Row key={index} item={item} index={index} />
            ))}
          </div>
        </div>
      </div>
      {/* mobile */}
      <div className={'flex flex-col gap-4 p-4 md:hidden'}>
        {mapLabelValue(options).map((item, index) => (
          <Row key={index} item={item} index={index} />
        ))}
      </div>
    </>
  );
};

SummaryIndividualHealthCard.propTypes = {
  effectDate: PropTypes.string,
  pack: PropTypes.object,
  getMiddleText: PropTypes.func,
  offerCode: PropTypes.string
};

export default SummaryIndividualHealthCard;
